import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, Sparkles, Crown, Lock, Library, ShieldCheck, X } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '@/api/axiosClient';
import { queryKeys } from '@/api/queryKeys';
import { getPlanFeatures, isYearlyPlan } from '@/config/plans';
import toast from 'react-hot-toast';
import { clearRecommendedPlan } from '@/lib/recommendedPlan';
import { trackEvent } from '@/lib/analytics';
import { getFunnelContext, INSTAGRAM_TRANSCRIPTION_FUNNEL } from '@/lib/funnelToken';

const STARTER_PLAN_SLUG = 'contentlife-starter-monthly';

interface PostAuthPlanModalProps {
  isOpen: boolean;
  recommendedPlan?: string;
  onClose?: () => void;
}

interface HubPlan {
  id: number | string;
  name: string;
  slug: string;
  description: string;
  price: number | string;
  billing_period: 'MONTHLY' | 'YEARLY';
  features: Record<string, boolean | number>;
  limits: Record<string, number>;
  is_popular?: boolean;
}

interface PlansResponse {
  plans: HubPlan[];
}

export default function PostAuthPlanModal({
  isOpen,
  recommendedPlan,
  onClose,
}: PostAuthPlanModalProps) {
  const { t } = useTranslation();
  const funnelContext = getFunnelContext();
  const isInstagramTranscriptionFunnel =
    funnelContext?.source === INSTAGRAM_TRANSCRIPTION_FUNNEL;

  // Auto-detect billing period from recommendedPlan slug
  const getInitialBillingPeriod = (): 'MONTHLY' | 'YEARLY' => {
    if (recommendedPlan) {
      if (recommendedPlan.includes('yearly') || recommendedPlan.includes('annual')) {
        return 'YEARLY';
      }
    }
    return 'MONTHLY';
  };

  const [billingPeriod, setBillingPeriod] = useState<'MONTHLY' | 'YEARLY'>(
    getInitialBillingPeriod
  );
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Reset billing period when recommendedPlan changes
  useEffect(() => {
    setBillingPeriod(getInitialBillingPeriod());
  }, [recommendedPlan]);

  // Fetch plans from Hub
  const { data: plansData, isLoading, refetch } = useQuery({
    queryKey: [...queryKeys.pricing, 'hub'],
    queryFn: async () => {
      const { data } = await axiosClient.get<PlansResponse>('/hub/billing/plans');
      return data;
    },
    enabled: isOpen,
  });

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: async (planSlug: string) => {
      const funnelQuery = isInstagramTranscriptionFunnel
        ? `&funnel=${INSTAGRAM_TRANSCRIPTION_FUNNEL}`
        : '';
      const { data } = await axiosClient.post<{ url: string }>(
        '/hub/billing/checkout',
        {
          plan_slug: planSlug,
          success_url: `${window.location.origin}/checkout/success?checkout=success${funnelQuery}`,
          cancel_url: `${window.location.origin}/dashboard?checkout=canceled${funnelQuery}`,
        }
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        if (!isInstagramTranscriptionFunnel) {
          clearRecommendedPlan();
        }
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Falha ao iniciar checkout');
      setLoadingPlan(null);
    },
  });

  if (!isOpen) return null;

  // Filter plans by billing period and exclude free, sorted by price
  const paidPlans =
    plansData?.plans
      .filter(
        (plan) => plan.billing_period === billingPeriod && !plan.slug.includes('free')
      )
      .sort((a, b) => {
        const priceA = typeof a.price === 'string' ? parseFloat(a.price) : a.price;
        const priceB = typeof b.price === 'string' ? parseFloat(b.price) : b.price;
        return priceA - priceB;
      }) || [];

  // Determine which plan to highlight
  const getRecommendedPlanSlug = (): string | null => {
    if (isInstagramTranscriptionFunnel) {
      const basePlan = STARTER_PLAN_SLUG.replace('-monthly', '').replace('-yearly', '');
      const targetSlug = `${basePlan}-${billingPeriod.toLowerCase()}`;
      if (paidPlans.some((p) => p.slug === targetSlug)) return targetSlug;
      if (paidPlans.some((p) => p.slug === STARTER_PLAN_SLUG)) return STARTER_PLAN_SLUG;
    }

    if (recommendedPlan) {
      // Extract base plan name and match with current billing period
      const basePlan = recommendedPlan
        .replace('-monthly', '')
        .replace('-yearly', '')
        .replace('-annual', '');
      const targetSlug = `${basePlan}-${billingPeriod.toLowerCase()}`;
      // Check if plan exists
      const exists = paidPlans.some((p) => p.slug === targetSlug);
      if (exists) return targetSlug;
      // Fallback: check if exact match exists
      if (paidPlans.some((p) => p.slug === recommendedPlan)) return recommendedPlan;
    }
    // Default: most expensive plan
    return paidPlans.length > 0 ? paidPlans[paidPlans.length - 1].slug : null;
  };

  const recommendedPlanSlug = getRecommendedPlanSlug();

  const handleSubscribe = (plan: HubPlan) => {
    setLoadingPlan(plan.slug);

    // Track begin_checkout event
    const price = typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price;
    trackEvent('begin_checkout', {
      plan_slug: plan.slug,
      billing_period: plan.billing_period,
      value: price,
      currency: 'BRL',
    });

    checkoutMutation.mutate(plan.slug);
  };

  const formatPrice = (price: number | string): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `R$${numPrice.toFixed(2).replace('.', ',')}`;
  };

  const getMonthlyEquivalent = (plan: HubPlan): string | null => {
    if (plan.billing_period === 'YEARLY') {
      const price = typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price;
      const monthly = price / 12;
      return `R$${monthly.toFixed(2).replace('.', ',')}/mes`;
    }
    return null;
  };

  const getFeatures = (plan: HubPlan): string[] => {
    const featureKeys = getPlanFeatures(plan.slug);
    const features = featureKeys.map((key) => t(key));
    if (isYearlyPlan(plan.slug)) {
      features.push(t('billing.features.twoMonthsFree'));
    }
    return features;
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-stretch justify-center lg:items-center lg:p-4">
      {/* Backdrop - no onClick handler */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative flex h-[100dvh] w-full max-w-7xl flex-col overflow-y-auto overscroll-contain rounded-none bg-surface shadow-2xl lg:h-auto lg:max-h-[95vh] lg:overflow-hidden lg:rounded-2xl">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            aria-label="Fechar popup de assinatura"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {/* Header */}
        <div className="border-b border-border bg-gradient-to-r from-primary to-primary-hover p-4 lg:p-5">
          <div className="text-center">
            <h2 className="flex items-center justify-center gap-2 text-xl font-bold text-white lg:text-2xl">
              <Crown className="h-5 w-5 lg:h-6 lg:w-6" />
              {isInstagramTranscriptionFunnel
                ? 'Em menos de 1 minuto, sua transcrição está pronta'
                : 'Escolha seu plano e teste por 7 dias'}
            </h2>
            <p className="mt-1 px-8 text-sm leading-snug text-white/80 lg:px-0 lg:text-base">
              {isInstagramTranscriptionFunnel
                ? 'Libere 100% do texto agora e escolha ferramentas para destravar sua produção de conteúdo'
                : 'Cancele dentro do trial se não quiser continuar'}
            </p>
          </div>
        </div>

        {isInstagramTranscriptionFunnel && (
          <div className="bg-secondary px-4 pt-4 lg:px-6 lg:pt-5">
            <div className="mx-auto max-w-6xl rounded-2xl border border-primary/20 bg-surface p-4 shadow-sm">
              <div className="grid gap-4 md:grid-cols-[1.2fr_0.8fr]">
                <div>
                  <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary lg:mb-3">
                    <Lock className="h-3.5 w-3.5" />
                    50% liberado agora — 100% após assinar
                  </div>
                  <p className="max-h-24 overflow-hidden whitespace-pre-wrap text-sm leading-relaxed text-foreground sm:max-h-40">
                    {funnelContext.previewText}
                  </p>
                  <div className="relative -mt-6 h-8 bg-gradient-to-t from-surface to-transparent lg:h-10" />
                  <p className="mt-1 flex items-center gap-2 text-sm font-medium text-muted-foreground lg:mt-2">
                    <Lock className="h-4 w-4" />
                    O restante da transcrição já está pronto e será liberado automaticamente.
                  </p>
                  <p className="mt-3 rounded-xl bg-accent/10 px-4 py-3 text-sm font-semibold leading-relaxed text-foreground">
                    Além da transcrição, você também pode acessar ferramentas para destravar sua produção de conteúdo:
                    biblioteca de inspirações, documentos, Kanban, prompts e IA conforme o plano escolhido.
                  </p>
                </div>
                <div className="hidden space-y-2 rounded-xl bg-primary/5 p-4 sm:block">
                  <div className="flex gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                    <div>
                      <p className="text-sm font-bold text-foreground">7 dias grátis</p>
                      <p className="text-xs text-muted-foreground">
                        Nada é cobrado hoje. Você testa antes da primeira cobrança no cartão.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                    <div>
                      <p className="text-sm font-bold text-foreground">Cancele quando quiser</p>
                      <p className="text-xs text-muted-foreground">
                        Cancelando dentro dos 7 dias, nada é cobrado.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Library className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                    <div>
                      <p className="text-sm font-bold text-foreground">Proteção contra abuso</p>
                      <p className="text-xs text-muted-foreground">
                        Pedimos cartão para liberar o teste completo e evitar uso automático em massa. Se cancelar dentro dos 7 dias, nada será cobrado.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Library className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                    <div>
                      <p className="text-sm font-bold text-foreground">Biblioteca de inspirações inclusa</p>
                      <p className="text-xs text-muted-foreground">
                        A transcrição completa será salva como inspiração para você reutilizar.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans intro */}
        <div className="bg-secondary px-4 pt-4 text-center lg:px-6 lg:pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Planos</p>
          <h3 className="mt-1 text-lg font-bold text-foreground lg:text-xl">
            Escolha como quer liberar sua transcrição
          </h3>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center bg-secondary px-4 pt-4 pb-0 lg:px-6 lg:pt-5">
          <div className="bg-border p-1 rounded-lg flex">
            <button
              onClick={() => setBillingPeriod('MONTHLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                billingPeriod === 'MONTHLY'
                  ? 'bg-surface shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingPeriod('YEARLY')}
              className={`px-6 py-2.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'YEARLY'
                  ? 'bg-surface shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anual
              <span className="bg-accent text-white text-xs px-2 py-0.5 rounded-full font-bold">
                2 meses gratis
              </span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="bg-secondary p-4 overflow-y-visible lg:flex-1 lg:overflow-y-auto lg:p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Carregando planos...</span>
            </div>
          ) : plansData ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {paidPlans.map((plan) => {
                const monthlyEquivalent = getMonthlyEquivalent(plan);
                const isYearly = plan.billing_period === 'YEARLY';
                const isRecommended = plan.slug === recommendedPlanSlug;

                return (
                  <div
                    key={plan.slug}
                    className={`relative bg-surface rounded-xl p-5 flex flex-col ${
                      isRecommended
                        ? 'shadow-lg border-2 border-primary ring-2 ring-primary/20'
                        : 'shadow-sm border border-border'
                    }`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                          RECOMENDADO
                        </span>
                      </div>
                    )}

                    <div
                      className={`flex items-center gap-2 mb-2 ${isRecommended ? 'mt-2' : ''}`}
                    >
                      <Crown
                        className={`w-5 h-5 ${isRecommended ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>

                    <div className="mt-4 flex items-baseline text-foreground">
                      {isYearly && monthlyEquivalent ? (
                        <>
                          <span
                            className={`text-4xl font-extrabold tracking-tight ${
                              isRecommended ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {monthlyEquivalent.replace('/mes', '')}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">
                            /mes
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            className={`text-4xl font-extrabold tracking-tight ${
                              isRecommended ? 'text-primary' : 'text-foreground'
                            }`}
                          >
                            {formatPrice(plan.price)}
                          </span>
                          <span className="ml-1 text-xl font-semibold text-muted-foreground">
                            /mes
                          </span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {isYearly
                        ? `Cobrado ${formatPrice(plan.price)} anualmente`
                        : 'Cobrado mensalmente'}
                    </p>
                    <p className="text-sm font-medium text-primary mt-2">
                      7 dias grátis. Depois, cobrança automática.
                    </p>

                    <ul className="mt-5 space-y-2.5 flex-1">
                      {getFeatures(plan).map((feature, idx) => (
                        <li key={idx} className="flex">
                          <Check className="flex-shrink-0 w-5 h-5 text-accent" />
                          <span className="ml-3 text-sm text-foreground font-medium">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={!!loadingPlan}
                      className={`mt-6 w-full py-3 px-4 rounded-lg text-sm font-bold text-white transition-all flex items-center justify-center ${
                        isRecommended
                          ? 'bg-primary hover:bg-primary-hover shadow-lg shadow-primary/30'
                          : 'bg-slate-900 hover:bg-slate-800'
                      } disabled:opacity-50`}
                    >
                      {loadingPlan === plan.slug ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {isInstagramTranscriptionFunnel && isRecommended
                            ? 'Liberar minha transcrição'
                            : 'Começar trial'}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                Nao foi possivel carregar os planos. Tente novamente.
              </p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
              >
                Tentar novamente
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-secondary border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            Pagamento seguro via Stripe. O trial exige cartão e pode ser cancelado antes da cobrança.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
